import { InfoText } from '@/components/atoms/InfoText/InfoText'
import { ViewAsToggle } from '@/components/molecules/ViewAsToggle/ViewAsToggle'
import useActiveManagementStore from '@/features/ActiveManagement/stores/useActiveManagementStore/useActiveManagementStore'
import { IntervalReportSettings } from '@/hooks'
import { ActiveManagementFiltersValues } from '@/utils/constants'
import { useAutoAnimate } from '@formkit/auto-animate/react'
import classNames from 'classnames'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IntervalView } from './IntervalView'

type IntervalReportProps = {
    activeFilters: ActiveManagementFiltersValues
    reportSettings: IntervalReportSettings
    setReportSettings?: (settings: Partial<IntervalReportSettings>) => void
    isOpen?: boolean
    calculationMethodOffset?: number
}

export const IntervalReport: React.FC<IntervalReportProps> = (props) => {
    const {
        activeFilters,
        reportSettings,
        setReportSettings: propsSetReportSettings,
        isOpen = false,
        calculationMethodOffset,
    } = props

    const [settings, setSettings] = useState(reportSettings)
    const [animationParent] = useAutoAnimate()
    const { t } = useTranslation('translations')
    const view = useActiveManagementStore(
        (state) => state.intervalData.selectedView
    )
    const setView = useActiveManagementStore(
        (state) => state.intervalData.setSelectedView
    )

    const setReportSettings = useCallback(
        (newSettings: Partial<IntervalReportSettings>) => {
            const typeSetting: Partial<IntervalReportSettings> = {}

            if (newSettings.display) {
                typeSetting['type'] =
                    newSettings.display === 'meters'
                        ? 'utilityData'
                        : 'subMeterData'
            }

            setSettings((oldSettings) => ({
                ...oldSettings,
                ...newSettings,
                ...typeSetting,
            }))
            propsSetReportSettings?.({ ...newSettings, ...typeSetting })
        },
        [propsSetReportSettings]
    )

    useEffect(() => {
        setSettings(reportSettings)
    }, [reportSettings])

    return (
        <div
            className="grid w-full grid-cols-[100%] grid-rows-[auto_1fr] gap-6 p-6"
            ref={animationParent}
        >
            <div className="flex items-center justify-between">
                <div className="flex items-center pt-1">
                    <div
                        className={classNames({
                            'transition-all duration-300 ease-out': isOpen,
                        })}
                        style={{
                            width:
                                calculationMethodOffset &&
                                `${String(calculationMethodOffset)}px`,
                        }}
                    />
                    <InfoText
                        text={t(
                            'active_management.interval.data_source_info.interval_readings.label'
                        )}
                        icon="info"
                        tooltipContent={t(
                            'active_management.interval.data_source_info.interval_readings.tooltip'
                        )}
                    />
                </div>
                <div className={classNames('flex items-center gap-4')}>
                    <ViewAsToggle
                        activeView={view}
                        onViewChange={setView}
                        data-testid="view-toggle-button"
                    />
                </div>
            </div>
            <IntervalView
                activeFilters={activeFilters}
                reportSettings={settings}
                setReportSettings={setReportSettings}
                activeView={view}
            />
        </div>
    )
}
